<script lang="ts">
    import { getContext } from "svelte";
    import type { Writable } from "svelte/store";
    import { type Puzzle, CellState } from "@lib/types";
    import { isSolved, validatePlacements } from "@lib/util";
    import { logEvent } from "@lib/events";
    import DotIcon from "@icons/Dot.svelte";
    import CrownIcon from "@icons/Crown.svelte";

    interface Props {
        state: number;
        index: number;
        readonly: boolean;
        pressing: boolean;
    }

    let { state, index, readonly, pressing = $bindable() }: Props = $props();

    let puzzle = getContext<Puzzle>("puzzle");
    let session = getContext<string>("session");
    let timer = getContext<Writable<{ paused: boolean; elapsed: number }>>("timer");
    let placements = getContext<Writable<Array<CellState>>>("placements");
    let snapshots = getContext<Writable<Array<Array<CellState>>>>("snapshots");
    let hintCells = getContext<Writable<Array<number>>>("hintCells");
    let heatmap = getContext<Writable<number[] | null>>("heatmap");
    let kingOrder = getContext<Writable<number[] | null>>("kingOrder");
    let markerRate = getContext<Writable<number[] | null>>("markerRate");

    const grid = puzzle.grid;
    const size = puzzle.size;
    const group = grid[index];

    const place = () => {
        if (readonly) return;

        const prev = $placements[index];
        placements.update((p: Array<CellState>) => {
            if (!isSolved(puzzle, p)) {
                switch (p[index]) {
                    case CellState.Empty:
                        p[index] = CellState.X;
                        break;
                    case CellState.X:
                        snapshots.update((s: Array<Array<CellState>>) => {
                            const copy = p.slice();
                            copy[index] = CellState.Empty;
                            s.push(copy);
                            return s;
                        });

                        p[index] = CellState.Star;
                        p = validatePlacements(puzzle, p);

                        break;
                    case CellState.Star:
                    case CellState.StarInvalid:
                        p[index] = CellState.Empty;
                        p = validatePlacements(puzzle, p);
                        break;
                }
            }
            return p;
        });

        const next = $placements[index];
        if (prev !== next) {
            const value =
                next === CellState.Star || next === CellState.StarInvalid
                    ? "king"
                    : next === CellState.X
                        ? "x"
                        : "empty";

            void logEvent({
                session,
                event_type: "cell_set",
                elapsed_ms: $timer.elapsed,
                data: { index, value },
            });
        }
    };

    const placeMarker = () => {
        if (readonly) return;

        const prev = $placements[index];
        placements.update((p: Array<CellState>) => {
            switch (p[index]) {
                case CellState.Empty:
                    p[index] = CellState.X;
                    break;
            }
            return p;
        });

        const next = $placements[index];
        if (prev !== next) {
            void logEvent({
                session,
                event_type: "cell_set",
                elapsed_ms: $timer.elapsed,
                data: { index, value: "x" },
            });
        }
    };

    const startPress = (e: PointerEvent) => {
        pressing = true;
        place();
        const target = e.target as HTMLElement | null;
        if (target) {
            target.releasePointerCapture(e.pointerId);
        }
    };
    const endPress = () => {
        pressing = false;
    };

    const borders = (group: number, neighbor: number) => {
        return group !== grid[neighbor];
    };

    const border =
        (borders(group, index - 1) || index % size === 0 ? " border-l" : "") +
        (borders(group, index - size) || index < size ? " border-t" : "") +
        (borders(group, index + 1) || index % size === size - 1 ? " border-r" : "") +
        (borders(group, index + size) || index > size ** 2 - size ? " border-b" : "");

    const corner =
        index == 0 ? "corner-tl" :
        index == size - 1 ? "corner-tr" :
        index == size * size - size ? "corner-bl" :
        index == size * size - 1 ? "corner-br" :
        "";

    const heatAlpha = () => {
        const h = $heatmap;
        if (!h) return 0;
        const v = h[index];
        if (typeof v !== "number" || !Number.isFinite(v)) return 0;
        return Math.max(0, Math.min(1, v));
    };

    const kingNum = () => {
        const o = $kingOrder;
        if (!o) return 0;
        const v = o[index];
        if (typeof v !== "number" || !Number.isFinite(v)) return 0;
        return Math.max(0, Math.floor(v));
    };

    const markerOpacity = () => {
        if (!readonly) return 0.6;
        const r = $markerRate;
        if (!r) return 0.6;
        const v = r[index];
        if (typeof v !== "number" || !Number.isFinite(v)) return 0.6;
        const rate = Math.max(0, Math.min(1, v));
        return 0.5 + 0.5 * rate;
    };

    const markerPct = () => {
        if (!readonly) return null;
        const r = $markerRate;
        if (!r) return null;
        const v = r[index];
        if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return null;
        const pct = Math.round(v * 100);
        if (pct <= 0) return "<1%";
        return `${pct}%`;
    };
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="cell cell-group-{group} {corner}"
    class:hint={$hintCells.includes(index)}
    onclick={(e) => e.preventDefault()}
    onpointerdown={startPress}
    onpointerup={endPress}
    onpointerover={() => {
        if (pressing) placeMarker();
    }}
>
    {#if heatAlpha() > 0}
        <div class="heat" style="opacity: {heatAlpha()};"></div>
    {/if}
    {#if state === 1}
        <div class="cell-content" style="width: 50%; opacity: {markerOpacity()};">
            <DotIcon fill="#030104" />
        </div>
    {:else if state === 2}
        <div class="cell-content king" style="width: 65%;">
            <CrownIcon fill="#000000" />
        </div>
        {#if kingNum() > 0}
            <div class="king-num monospace">{kingNum()}</div>
        {/if}
    {:else if state === 3}
        <div class="cell-content king" style="width: 65%;">
            <CrownIcon fill="#dc2626" />
        </div>
        {#if kingNum() > 0}
            <div class="king-num monospace">{kingNum()}</div>
        {/if}
    {:else if state === 4}
        <div class="cell-content king" style="width: 65%;">
            <CrownIcon fill="#b45309" />
        </div>
        {#if kingNum() > 0}
            <div class="king-num monospace">{kingNum()}</div>
        {/if}
    {/if}
    {#if state === CellState.X && markerPct()}
        <div class="marker-tip monospace">{markerPct()}</div>
    {/if}
    <div class="cell-border {border}"></div>
</div>

<style>
    .heat {
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(circle at 50% 50%, var(--accent-color) 0%, transparent 70%);
    }

    .king-num {
        position: absolute;
        top: 3px;
        right: 4px;
        font-size: 0.75rem;
        line-height: 1;
        background-color: rgba(255, 255, 255, 0.75);
        color: #000;
        border-radius: 999px;
        padding: 2px 6px;
        pointer-events: none;
    }

    :global(body.dark) .king-num {
        background-color: rgba(0, 0, 0, 0.6);
        color: #fff;
    }

    .marker-tip {
        position: absolute;
        left: 4px;
        bottom: 3px;
        font-size: 0.7rem;
        line-height: 1;
        background-color: rgba(255, 255, 255, 0.75);
        color: #000;
        border-radius: 6px;
        padding: 2px 5px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.12s ease;
    }

    .cell:hover .marker-tip {
        opacity: 1;
    }

    :global(body.dark) .marker-tip {
        background-color: rgba(0, 0, 0, 0.6);
        color: #fff;
    }

</style>
